/* eslint-disable @typescript-eslint/no-var-requires */
/* eslint-disable global-require */
/* eslint-disable no-nested-ternary */
import { CenterTypeEnum, ProfessionalTypeEnum } from '@gql/graphql';
import {
  Accordion,
  Button,
  DetailListContact,
  DetailListProPractical,
  DetailListSchedule,
  HeaderProfile,
  PartnerInfoBox,
  RelatedLinks,
  Section,
  SectionSubtitle,
  SectionText,
  SectionTitle,
  SideLayoutContentWrapper,
  Slideshow,
  TranslatableContent,
  TranslateButton,
  StoreBanner,
  WhatsappButton,
} from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import { useCurrentAccount } from '@application/api';
import type { FC } from 'react';
import { Suspense, lazy, useMemo } from 'react';
import StickyBox from 'react-sticky-box';
import type { ProPageProps } from '..';
import agenda from '../ProLanding/assets/agenda-0-bg.webp';
import { ProContactInfos } from './components/ProContactInfos';
import * as Styled from './styled';

export * from './types';

const Map = lazy(() =>
  import('../../components/Map').then((module) => ({ default: module.default })),
);

export const ProPage: FC<ProPageProps> = (props) => {
  const {
    slideshow,
    serviceSections,
    contactInfo,
    practicalInfo,
    addressCoordinates,
    header,
    description,
    wellnessEngagement,
    BookingForm,
    proSchedule,
    shopSchedule,
    urgencyAvailabilities,
    onlineBookingEnabled = false,
    proType,
    proCenterType,
    showContactInfo = false,
    onShowContactInfo,
    onOverlayClick,
    onSideCardClick,
    showOverlay = false,
    isUnclaimed = false,
    extraDescription,
    unclaimedLink,
    relatedLinks,
    isTipawPartner,
    tipawPartnerRef,
    enableWhatsAppContact = false,
    onWhatsappClick,
  } = props;
  const { translate } = useTranslation();
  const { currentAccountType } = useCurrentAccount();

  const shouldDisplayBookingForm = useMemo(() => {
    return onlineBookingEnabled && currentAccountType !== 'breeder';
  }, [onlineBookingEnabled, currentAccountType]);

  return (
    <>
      <HeaderProfile {...header} withWhatsapp={enableWhatsAppContact} />
      <Styled.PageContainer>
        <Styled.SideLayoutWrapper>
          <SideLayoutContentWrapper>
            {slideshow?.items?.length ? (
              <Styled.SlideshowSeparator>
                <Slideshow {...slideshow} />
              </Styled.SlideshowSeparator>
            ) : null}

            {isUnclaimed && (
              <Section>
                <Styled.UnclaimedCard>
                  <Styled.UnclaimedCardText>
                    {translate('proDetails.unclaimed.text').replace(
                      '$name',
                      typeof header?.title === 'string'
                        ? header.title
                        : translate('proDetails.unclaimed.nameFallback'),
                    )}
                  </Styled.UnclaimedCardText>
                  <Button shrink color="blue" link={unclaimedLink}>
                    {translate('common.buttons.claimPro')}
                  </Button>
                </Styled.UnclaimedCard>
              </Section>
            )}

            <Section>
              {description && (
                <>
                  <SectionTitle>
                    {proType === ProfessionalTypeEnum.GROOMER
                      ? translate('proDetails.description.groomer')
                      : translate(
                          `proDetails.description.vet.${
                            proCenterType?.toUpperCase() ?? CenterTypeEnum.CABINET
                          }`,
                        )}
                  </SectionTitle>
                  <Styled.TranslateButtonContainer>
                    <TranslateButton source="pro" />
                  </Styled.TranslateButtonContainer>
                  <TranslatableContent
                    content={description}
                    format="html"
                    DisplayComponent={SectionText}
                  />
                </>
              )}
              {wellnessEngagement && (
                <>
                  <SectionSubtitle>{translate('glossary.engagement')}</SectionSubtitle>
                  <TranslatableContent
                    content={wellnessEngagement}
                    format="html"
                    DisplayComponent={SectionText}
                  />
                </>
              )}
            </Section>
            {!!serviceSections && (
              <Section>
                <SectionTitle>{translate('glossary.services')}</SectionTitle>
                <Accordion
                  noScroll
                  defaultChecked
                  tabs={serviceSections.map((serviceSection) => ({
                    title: serviceSection.title,
                    defaultOpen: true,
                    content: (
                      <>
                        {serviceSection.items.map((service, index) => (
                          <Styled.ServiceItem index={index} key={index}>
                            <Styled.ServiceLabel link={!!service.onClick} onClick={service.onClick}>
                              {service.name}
                              {service.onClick && <Styled.ServiceChevronRight />}
                              {!!service.isSpecialist && (
                                <Styled.ServiceSpecialistTag>
                                  {translate('glossary.specialist')}
                                </Styled.ServiceSpecialistTag>
                              )}
                            </Styled.ServiceLabel>
                            <Styled.ServiceRightWrapper>
                              <Styled.ServiceDuration>{service.duration}</Styled.ServiceDuration>
                              {proType === ProfessionalTypeEnum.GROOMER && !!service.price && (
                                <Styled.ServicePrice>{service.price}</Styled.ServicePrice>
                              )}
                            </Styled.ServiceRightWrapper>
                          </Styled.ServiceItem>
                        ))}
                      </>
                    ),
                  }))}
                />
              </Section>
            )}
            {!!practicalInfo && (
              <Section id="information-section">
                <SectionTitle>{translate('glossary.infoPratique')}</SectionTitle>
                <DetailListProPractical {...practicalInfo} proType={proType || 'VET'} />
              </Section>
            )}
            {(addressCoordinates || shouldDisplayBookingForm) && (
              <Section>
                {shouldDisplayBookingForm && (
                  <>
                    <SectionTitle>{translate('glossary.contact')}</SectionTitle>
                    <DetailListContact
                      {...contactInfo}
                      blurred={!showContactInfo}
                      blurredChildren={
                        <Button color="blue" shrink onClick={onShowContactInfo} size="large">
                          {translate('common.buttons.getCoords')}
                        </Button>
                      }
                    />
                  </>
                )}
                {addressCoordinates?.lat && addressCoordinates?.lng && !!Map && (
                  <Styled.MapContainer>
                    <Suspense fallback="...">
                      <Map
                        style={{ height: '400px' }}
                        center={{ ...addressCoordinates }}
                        zoom={12}
                      />
                    </Suspense>
                  </Styled.MapContainer>
                )}
              </Section>
            )}

            {extraDescription && (
              <TranslatableContent
                content={extraDescription}
                format="html"
                DisplayComponent={SectionText}
              />
            )}

            {(proSchedule || shopSchedule || urgencyAvailabilities) && (
              <Section>
                <SectionTitle>{translate('glossary.schedule')}</SectionTitle>
                <Accordion
                  defaultChecked
                  noScroll
                  tabs={[
                    ...(!proSchedule
                      ? []
                      : [
                          {
                            title: translate('glossary.bookingSchedule'),
                            content: <DetailListSchedule {...proSchedule} />,
                          },
                        ]),
                    ...(!shopSchedule
                      ? []
                      : [
                          {
                            title: translate('glossary.openHours'),
                            content: <DetailListSchedule {...shopSchedule} />,
                          },
                        ]),
                    ...(!urgencyAvailabilities
                      ? []
                      : [
                          {
                            title: translate('glossary.urgency'),
                            content: <DetailListSchedule {...urgencyAvailabilities} />,
                          },
                        ]),
                  ]}
                />
              </Section>
            )}

            {isUnclaimed && (
              <Section>
                <Styled.UnclaimedCard>
                  <Styled.StyledProLandingSectionContent
                    title={translate('common.sections.areYouPro.title')}
                    contents={[
                      {
                        subtitle: translate('common.sections.areYouPro.subtitle1'),
                        text: translate('common.sections.areYouPro.text1'),
                      },
                    ]}
                    image={agenda}
                  >
                    <Button color="blue" shrink link={unclaimedLink}>
                      {translate('common.buttons.claimPro')}
                    </Button>
                  </Styled.StyledProLandingSectionContent>
                </Styled.UnclaimedCard>
              </Section>
            )}
          </SideLayoutContentWrapper>

          {showOverlay && <Styled.Overlay onClick={onOverlayClick} />}

          <Styled.StyledSideLayoutSideWrapper>
            <StickyBox offsetTop={85}>
              <Styled.ClickableSideLayoutSideWrapper onClick={onSideCardClick}>
                {shouldDisplayBookingForm ? (
                  BookingForm
                ) : (
                  <ProContactInfos
                    {...props}
                    enableWhatsAppContact={enableWhatsAppContact}
                    onWhatsappClick={onWhatsappClick}
                  />
                )}
                {isTipawPartner && (
                  <div ref={tipawPartnerRef}>
                    <PartnerInfoBox
                      title={translate('proDetails.partner.title')}
                      content={translate('proDetails.partner.content')}
                    />
                  </div>
                )}
                {isUnclaimed ? (
                  <Styled.StoreBannerContainer>
                    <StoreBanner source="pro" theme="lightBlue" useMobileImage />
                  </Styled.StoreBannerContainer>
                ) : null}
              </Styled.ClickableSideLayoutSideWrapper>
              {enableWhatsAppContact && shouldDisplayBookingForm ? (
                <Styled.WhatsAppContainer>
                  <WhatsappButton outlined={false} onClick={onWhatsappClick} />
                </Styled.WhatsAppContainer>
              ) : null}
            </StickyBox>
          </Styled.StyledSideLayoutSideWrapper>
        </Styled.SideLayoutWrapper>
      </Styled.PageContainer>
      {relatedLinks && (
        <Styled.RelatedLinksContainer>
          <RelatedLinks {...relatedLinks} />
        </Styled.RelatedLinksContainer>
      )}
    </>
  );
};
